import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Button, Stack, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { ensureGraph, flowGraphToReactFlow, reactFlowToFlowGraph } from "./graphModel";
import FlowNode from "./FlowNode";
import NodeConfigPanel from "./NodeConfigPanel";

const nodeTypes = { xproFlowNode: FlowNode };

function uid(prefix = "n") {
  return `${prefix}_${Math.random().toString(16).slice(2, 8)}_${Date.now().toString(16)}`;
}

export default function FlowCanvas({ config, onChangeConfig }) {
  const cfgWithGraph = useMemo(() => ensureGraph(config || {}), [config]);
  const rfInitial = useMemo(() => flowGraphToReactFlow(cfgWithGraph.graph), [cfgWithGraph.graph]);

  const [nodes, setNodes] = useState(rfInitial.nodes);
  const [edges, setEdges] = useState(rfInitial.edges);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const selectedNode = useMemo(() => nodes.find((n) => String(n.id) === String(selectedNodeId)) || null, [nodes, selectedNodeId]);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  useEffect(() => {
    setNodes(rfInitial.nodes);
    setEdges(rfInitial.edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfInitial.nodes.length, rfInitial.edges.length, cfgWithGraph.graph]);

  const emitConfig = useCallback(
    (nextNodes, nextEdges) => {
      const flowGraph = reactFlowToFlowGraph(nextNodes, nextEdges);
      const next = {
        ...(config && typeof config === "object" ? config : {}),
        graph: flowGraph,
      };
      onChangeConfig?.(next);
    },
    [config, onChangeConfig]
  );

  const onConnect = useCallback(
    (connection) => {
      setEdges((prev) => {
        const next = addEdge({ ...connection, type: "smoothstep" }, prev);
        emitConfig(nodesRef.current, next);
        return next;
      });
    },
    [emitConfig, setEdges]
  );

  const addNode = (type) => {
    const id = uid(type.toLowerCase());
    const base = {
      id,
      type: "xproFlowNode",
      position: { x: 120, y: 120 },
      data: {
        nodeType: type,
        label:
          type === "Condition"
            ? "Condição"
            : type === "Delay"
              ? "Aguardar"
              : type === "ChannelMessage"
                ? "Enviar mensagem"
                : type,
        rawConfig: {},
        onRename: (nodeId, nextLabel) => {
          setNodes((prev) => {
            const out = prev.map((n) =>
              n.id === nodeId ? { ...n, data: { ...n.data, label: String(nextLabel) } } : n
            );
            emitConfig(out, edges);
            return out;
          });
        },
      },
    };
    setNodes((prev) => {
      const out = [...prev, base];
      emitConfig(out, edges);
      return out;
    });
  };

  const onChangeNode = (nodeId, patch) => {
    setNodes((prev) => {
      const out = prev.map((n) => {
        if (String(n.id) !== String(nodeId)) return n;
        const nextData = {
          ...n.data,
          ...(patch || {}),
          label: patch?.label != null ? String(patch.label) : n.data?.label,
          nodeType: patch?.nodeType != null ? String(patch.nodeType) : n.data?.nodeType,
          rawConfig: patch?.rawConfig && typeof patch.rawConfig === "object" ? patch.rawConfig : n.data?.rawConfig,
        };
        return { ...n, data: nextData };
      });
      emitConfig(out, edges);
      return out;
    });
  };

  const onDeleteNode = (nodeId) => {
    setNodes((prev) => {
      const outNodes = prev.filter((n) => String(n.id) !== String(nodeId));
      setEdges((prevEdges) => {
        const outEdges = prevEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
        emitConfig(outNodes, outEdges);
        return outEdges;
      });
      setSelectedNodeId(null);
      return outNodes;
    });
  };

  return (
    <Box sx={{ width: "100%", minHeight: 520, height: "calc(100vh - 280px)", position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          p: 1,
          borderRadius: 2,
          bgcolor: "background.paper",
          boxShadow: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" fontWeight={900}>
          Builder visual
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addNode("CreateLead")}>
            Lead
          </Button>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addNode("Tag")}>
            Tag
          </Button>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addNode("ChannelMessage")}>
            Mensagem
          </Button>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addNode("Condition")}>
            Condição
          </Button>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addNode("Delay")}>
            Delay
          </Button>
        </Stack>
      </Box>

      <ReactFlow
        nodes={nodes.map((n) => ({
          ...n,
          data: {
            ...n.data,
            onRename: (nodeId, nextLabel) => {
              setNodes((prev) => {
                const out = prev.map((x) => (x.id === nodeId ? { ...x, data: { ...x.data, label: String(nextLabel) } } : x));
                emitConfig(out, edges);
                return out;
              });
            },
          },
        }))}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={(changes) => {
          setNodes((prev) => {
            const next = applyNodeChanges(changes, prev);
            emitConfig(next, edgesRef.current);
            return next;
          });
        }}
        onEdgesChange={(changes) => {
          setEdges((prev) => {
            const next = applyEdgeChanges(changes, prev);
            emitConfig(nodesRef.current, next);
            return next;
          });
        }}
        onConnect={onConnect}
        onNodeClick={(_, n) => setSelectedNodeId(n.id)}
        fitView
        minZoom={0.25}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: "smoothstep",
          style: { strokeWidth: 2, stroke: "rgba(0,0,0,0.45)" },
        }}
      >
        <Controls />
        <MiniMap />
        <Background color="#e0e0e0" gap={16} />
      </ReactFlow>

      <NodeConfigPanel
        open={!!selectedNode}
        node={selectedNode}
        onClose={() => setSelectedNodeId(null)}
        onChangeNode={onChangeNode}
        onDeleteNode={onDeleteNode}
      />
    </Box>
  );
}

